import { ConfigService } from '@axo/config/ConfigService';
import { reloadData } from '@axo/form/data-access/backend-sync';
import { Purpose } from '@axo/shared/data-access/types';
import { useTranslation } from '@axo/shared/i18n';
import { ComponentProps, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { ExtendedLoanOverview } from '../../../../../../components/ExtendedLoanOverview/ExtendedLoanOverview';
import { useAxoFormContext } from '../../../../../../context/AxoForm/AxoForm';
import { getAllocationPriority } from '../../../../../../utils/Finland/getAllocationPriority';
import { FinlandFormData } from '../../../../FormData.fi.types';

export const LoanOverview = () => {
  const { t } = useTranslation();

  const { applicationId } = reloadData.get();

  const { watch, setValue } = useFormContext();

  const { axoFormData, onChange } = useAxoFormContext<FinlandFormData>();

  const [creditCardDebt, personalDebt] = watch([
    'Debt/CreditCard/OutstandingAmount',
    'Debt/Personal/OutstandingAmount',
  ]).map(Number);

  const extraProps = useMemo(
    (): ComponentProps<
      typeof ExtendedLoanOverview
    >['refinanceableDebtsExtraProps'] => ({
      shouldUseStoredRefinanceableDebts: true,
      isTotalAmountVisible: true,
      editType: 'toggle',
    }),
    []
  );

  const refinanceableDebts = useMemo(
    () =>
      [
        {
          key: 'creditCardDebt',
          label: 'Luottokorttivelka',
          valuePath: 'Debt/CreditCard/OutstandingAmount',
          value: creditCardDebt,
          step: 250,
          isChecked: true,
        },
        {
          key: 'unsecuredDebt',
          label: 'Vakuudettomat velat',
          valuePath: 'Debt/Personal/OutstandingAmount',
          step: 250,
          value: personalDebt,
          isChecked: true,
        },
      ].filter(({ value }) => value > 0),
    [creditCardDebt, personalDebt]
  );

  if (!creditCardDebt && !personalDebt) return null;

  const allocationPriority = getAllocationPriority({
    creditCardDebt: creditCardDebt,
    unsecuredDebt: personalDebt,
  });

  const {
    amount: {
      default: DEFAULT_LOAN_AMOUNT,
      max: MAX_LOAN_AMOUNT,
      min: MIN_LOAN_AMOUNT,
    },
    duration: { default: DEFAULT_LOAN_DURATION },
  } = ConfigService.get('product-unsecured-loan').config;

  const appliedAmount =
    axoFormData['LoanApplication/AppliedAmount'] ?? DEFAULT_LOAN_AMOUNT;
  const loanDuration =
    axoFormData['LoanApplication/LoanDuration'] ?? DEFAULT_LOAN_DURATION;

  const isRefinancing =
    axoFormData['LoanApplication/InitialPurpose'] === Purpose.RefinancingLoan;

  const labels = t('LoanOverview', { returnObjects: true }) as ComponentProps<
    typeof ExtendedLoanOverview
  >['labels'];

  const onRefinanceAmountChange: ComponentProps<
    typeof ExtendedLoanOverview
  >['onRefinanceAmountChange'] = ({ refinanceAmount: newValue }) => {
    if (!newValue || newValue < 0) {
      onChange({
        'Debt/Personal/RefinanceAmount': 0,
        'LoanApplication/TotalRefinancingAmount': 0,
        'LoanApplication/Purpose': Purpose.Other,
      });
      setValue('LoanApplication/Purpose', Purpose.Other);
      return;
    }

    onChange({
      'Debt/Personal/RefinanceAmount': newValue,
      'LoanApplication/TotalRefinancingAmount': newValue,
      'LoanApplication/Purpose': Purpose.RefinancingLoan,
    });
    setValue('LoanApplication/Purpose', Purpose.RefinancingLoan);
  };

  const onTotalAmountChange = (newValue: number) => {
    if (axoFormData['LoanApplication/AppliedAmount'] === newValue) return;
    if (newValue > MAX_LOAN_AMOUNT) newValue = MAX_LOAN_AMOUNT;
    if (newValue < MIN_LOAN_AMOUNT) newValue = MIN_LOAN_AMOUNT;

    onChange({ 'LoanApplication/AppliedAmount': newValue });
    setValue('LoanApplication/AppliedAmount', newValue, {
      shouldTouch: true,
      shouldDirty: true,
    });
  };

  const onLoanDurationChange = (newValue: number) => {
    if (newValue <= 0) return;
    if (axoFormData['LoanApplication/LoanDuration'] === newValue) return;
    onChange({ 'LoanApplication/LoanDuration': newValue });
  };

  return (
    <ExtendedLoanOverview
      loanApplicationID={applicationId || undefined}
      labels={labels}
      currency="€"
      appliedAmount={appliedAmount}
      loanDuration={loanDuration}
      minLoanAmount={MIN_LOAN_AMOUNT}
      maxLoanAmount={MAX_LOAN_AMOUNT}
      isUpSell={!isRefinancing}
      allocationPriority={allocationPriority}
      onTotalAmountChange={onTotalAmountChange}
      onLoanDurationChange={onLoanDurationChange}
      isDebtInfoButtonVisible={false}
      isLoanDurationVisible={false}
      isEditButtonsVisible={false}
      initialEditField="cashAmount"
      refinanceableDebts={refinanceableDebts}
      refinanceableDebtsExtraProps={extraProps}
      onRefinanceAmountChange={onRefinanceAmountChange}
      // debounced change on page load doesn't trigger onRefinanceAmountChange
      debounceRefinanceAmountChange={false}
    />
  );
};
